import { ADMINISTRATION_KEY } from "./dataMappers";

/** Frontend department id for Operations — field staff use branches; HQ uses sections */
export const OPS_DEPT_ID = "operations";

/** Frontend department id for Marketing. */
export const MKT_DEPT_ID = "marketing";

export const OPS_ONLY_MEMBER_TYPES = [
  "branch-manager",
  "regional-manager",
  "supervisor",
];

export function isOperationsDepartment(departmentId) {
  return departmentId === OPS_DEPT_ID;
}

export function isMarketingDepartment(departmentId) {
  if (!departmentId) return false;
  const key = String(departmentId).trim().toLowerCase();
  return key === MKT_DEPT_ID || key === "mkt";
}

/** Frontend department id for Accounts (الحسابات). */
export const ACC_DEPT_ID = "accounts";

export function isAccountsDepartment(departmentId) {
  return departmentId === ACC_DEPT_ID || departmentId === "ACC";
}

export function departmentUsesBranches(departmentId) {
  return isOperationsDepartment(departmentId);
}

/** Cashiers live under Accounts but are assigned to a field branch. */
export function isCashierJobTitle(jobTitle) {
  return jobTitle === "كاشير";
}

export function isCashierManagerJobTitle(jobTitle) {
  return jobTitle === "مدير الكاشيرية";
}

export function isModeratorJobTitle(jobTitle) {
  if (!jobTitle) return false;
  const compact = String(jobTitle).replace(/\s+/g, "");
  const lower = compact.toLowerCase();
  return (
    compact.includes("موديريتور") ||
    compact.includes("مودريتور") ||
    compact.includes("موديتراتور") ||
    lower.includes("moderator")
  );
}

export function employeeRequiresShiftHandover(emp) {
  return Boolean(emp?.requiresShiftHandover) || isModeratorJobTitle(emp?.jobTitle);
}

/** HQ تيم ليدر prepares the moderator weekly plan (not a field-branch TL). */
export function isModeratorTeamLeaderJobTitle(jobTitle) {
  if (!jobTitle) return false;
  const compact = String(jobTitle).replace(/[\s\-_\/]+/g, "");
  const lower = compact.toLowerCase();
  return compact.includes("تيمليدر") || lower.includes("teamleader");
}

export function isModeratorTimesheetLeaderUser(user) {
  if (!user) return false;
  if (user.branchId) return false;
  return isModeratorTeamLeaderJobTitle(user.jobTitle);
}

/**
 * HQ تيم ليدر الموديريتور (no field branch) reports to Marketing leadership,
 * not Operations branch / section managers.
 */
export function isHqModeratorTeamLeaderAssignment({
  jobTitle,
  branchId,
} = {}) {
  if (!isModeratorTeamLeaderJobTitle(jobTitle)) return false;
  return isAdministrationAssignment(branchId);
}

export function isMarketingLeadershipMember(emp) {
  if (!emp || emp.status === "terminated") return false;
  if (!isMarketingDepartment(emp.departmentId)) return false;
  return (
    emp.memberLevel === "department-manager" ||
    emp.memberLevel === "section-manager"
  );
}

/**
 * Whether the HR form should collect a field branch:
 * OPS field staff, or cashiers (any department) for timesheets / advances.
 */
export function assignmentUsesBranch({
  departmentId,
  memberType,
  jobTitle,
} = {}) {
  if (isCashierJobTitle(jobTitle) && (!memberType || memberType === "employee")) {
    return true;
  }
  return (
    departmentUsesBranches(departmentId) &&
    memberType !== "regional-manager" &&
    memberType !== "department-manager" &&
    memberType !== "section-manager"
  );
}

export function isOpsOnlyMemberType(memberType) {
  return OPS_ONLY_MEMBER_TYPES.includes(memberType);
}

/**
 * Field supervisors / branch / regional managers stay Operations-only.
 * HQ تيم ليدر الموديريتور may live under Marketing (or another HQ department).
 */
export function isOpsLockedMemberType(memberType, assignment = {}) {
  if (!isOpsOnlyMemberType(memberType)) return false;
  if (isHqModeratorTeamLeaderAssignment(assignment)) return false;
  return true;
}

/** HQ / administration assignment (no field branch) — used for OPS sections like المخزن */
export function isAdministrationAssignment(branchId) {
  return !branchId || branchId === ADMINISTRATION_KEY;
}

export function getMemberTypesForDepartment(departmentId, memberTypes) {
  if (isOperationsDepartment(departmentId)) {
    return memberTypes;
  }
  return memberTypes.filter((type) => !OPS_ONLY_MEMBER_TYPES.includes(type.id));
}

/** Departments valid for a given member/rank type (list filters + forms). */
export function getDepartmentsForMemberType(memberType, departments) {
  if (!memberType) return departments;
  if (isOpsOnlyMemberType(memberType)) {
    return departments.filter((dept) => isOperationsDepartment(dept.id));
  }
  // section-manager is allowed in every department, including Operations HQ sections
  return departments;
}

export function getStructureNavLabel(userRole, departmentId) {
  if (userRole === "gm" || userRole === "owner" || userRole === "admin") return "الهيكل التنظيمي";
  if (userRole === "hr" || userRole === "hr-staff") return "الهيكل التنظيمي";
  if (userRole !== "department-manager") return "الفروع والأقسام";
  return isOperationsDepartment(departmentId) ? "الفروع والأقسام" : "الأقسام";
}

/** OPS employees who belong to HQ sections (not field branches). */
export function isOpsHeadquartersEmployee(emp) {
  if (!emp || !isOperationsDepartment(emp.departmentId)) return false;
  if (
    emp.memberLevel === "section-manager" ||
    emp.memberLevel === "department-manager" ||
    emp.memberLevel === "gm"
  ) {
    return true;
  }
  if (
    emp.memberLevel === "supervisor" ||
    emp.memberLevel === "branch-manager" ||
    emp.memberLevel === "regional-manager"
  ) {
    return false;
  }
  return isAdministrationAssignment(emp.branchId);
}

function sectionDisplayName(manager) {
  const title = manager.jobTitle?.trim();
  if (title && title !== "مدير قسم") return title;
  if (title) return `${title} — ${manager.name}`;
  return manager.name || "قسم";
}

/**
 * Group department employees into real sections (non-ops + OPS HQ):
 * each section-manager + everyone in their reporting chain,
 * with the department manager listed alongside the team.
 * People without a section manager stay under "الإدارة".
 */
export function buildSectionGroups(employees = []) {
  if (!employees.length) return [];

  const byId = new Map(employees.map((emp) => [String(emp.id), emp]));
  const departmentManagers = employees.filter(
    (emp) =>
      emp.memberLevel === "department-manager" || emp.memberLevel === "gm",
  );
  const sectionManagers = employees.filter(
    (emp) => emp.memberLevel === "section-manager",
  );

  function resolveSectionManagerId(emp) {
    if (emp.memberLevel === "section-manager") return String(emp.id);

    let current = emp;
    const visited = new Set();
    while (current?.managerId) {
      const managerId = String(current.managerId);
      if (visited.has(managerId)) break;
      visited.add(managerId);

      const manager = byId.get(managerId);
      if (!manager) break;
      if (manager.memberLevel === "section-manager") return String(manager.id);
      if (
        manager.memberLevel === "department-manager" ||
        manager.memberLevel === "gm"
      ) {
        break;
      }
      current = manager;
    }

    return null;
  }

  if (sectionManagers.length === 0) {
    const listed = employees.filter(
      (emp) => emp.memberLevel !== "regional-manager",
    );
    if (!listed.length) return [];
    return [
      {
        id: "department",
        name: "الإدارة",
        employees: listed,
      },
    ];
  }

  const groups = new Map();
  sectionManagers.forEach((manager) => {
    groups.set(String(manager.id), {
      id: String(manager.id),
      name: sectionDisplayName(manager),
      employees: [],
    });
  });

  const unassigned = [];

  employees.forEach((emp) => {
    if (
      emp.memberLevel === "department-manager" ||
      emp.memberLevel === "gm" ||
      emp.memberLevel === "regional-manager"
    ) {
      return;
    }

    const sectionId = resolveSectionManagerId(emp);
    if (sectionId && groups.has(sectionId)) {
      groups.get(sectionId).employees.push(emp);
      return;
    }

    unassigned.push(emp);
  });

  const sections = Array.from(groups.values())
    .filter((section) => section.employees.length > 0)
    .map((section) => ({
      ...section,
      employees: [...departmentManagers, ...section.employees],
    }));

  if (unassigned.length > 0 || sections.length === 0) {
    sections.unshift({
      id: "administration",
      name: "الإدارة",
      employees: [...departmentManagers, ...unassigned],
    });
  }

  return sections;
}

export function filterOpsBranchEmployees(employees = []) {
  return employees.filter((emp) => isOperationsDepartment(emp.departmentId));
}
